import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DemAnalyticsCard,
  DemAnalyticsCffStats,
  DemAnalyticsChannelSummary,
  DemAnalyticsDeliveryStats,
  DemAnalyticsDonut,
  DemAnalyticsLeaderboardChart,
  DemAnalyticsHourlyChannelChart,
  DemAnalyticsTrendChart,
  DemAnalyticsUserChannelChart,
  DemAnalyticsUserEfficiencyTable,
  DemAnalyticsYearBarChart,
} from '../../components/dem/DemAnalyticsCharts';
import {
  addDaysYmd,
  buildCffPersonStats,
  buildDeliveryCreatorStats,
  buildDeliveryIssueCausedByStats,
  buildDeliveryMonthVolumes,
  buildDeliveryRequestedByStats,
  buildHourlyChannelTrend,
  buildLast7DaysTrend,
  buildMonthlyTrendForYear,
  buildChannelTotals,
  buildStatusBreakdown,
  buildUserKpi,
  buildYearLeaderboard,
  buildYearTotals,
  exportDemAnalyticsOrders,
  fetchDemAnalyticsCff,
  fetchDemAnalyticsDeliveries,
  fetchDemAnalyticsOrders,
  filterCffByDate,
  filterCffByYear,
  filterCffByYearMonth,
  filterDeliveriesByDate,
  filterDeliveriesByYear,
  filterDeliveriesByYearMonth,
  filterOrdersByDate,
  filterOrdersByYear,
  filterOrdersByYearMonth,
  formatDisplayDate,
  monthLabel,
  peakHourSummary,
  singaporeYmd,
  singaporeYearMonth,
  trendLabelShort,
  yearOptions,
} from '../../lib/demAnalytics';
import { ensureSecondaryAuthSynced } from '../../lib/supabase';
import type {
  DemAnalyticsCffRow,
  DemAnalyticsDeliveryRow,
  DemAnalyticsOrder,
} from '../../types/dem';

type AnalyticsTab = 'daily' | 'monthly' | 'yearly';

const TAB_ITEMS: { id: AnalyticsTab; label: string }[] = [
  { id: 'daily', label: 'Daily' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'yearly', label: 'Yearly' },
];

function displayMonthYear(year: number, month: number): string {
  return `${monthLabel(month)}'${year}`;
}

export function DemAnalyticsPage() {
  const today = singaporeYmd();
  const { year: currentYear, month: currentMonth } = singaporeYearMonth();

  const [tab, setTab] = useState<AnalyticsTab>('daily');
  const [orders, setOrders] = useState<DemAnalyticsOrder[]>([]);
  const [deliveries, setDeliveries] = useState<DemAnalyticsDeliveryRow[]>([]);
  const [cffRows, setCffRows] = useState<DemAnalyticsCffRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const [dailyDate, setDailyDate] = useState(today);
  const [monthlyYear, setMonthlyYear] = useState(currentYear);
  const [monthlyMonth, setMonthlyMonth] = useState(currentMonth);
  const [yearlyYear, setYearlyYear] = useState(currentYear);

  const years = useMemo(() => yearOptions(), []);
  const hasData = orders.length > 0 || deliveries.length > 0 || cffRows.length > 0;

  const load = useCallback(async (force = false) => {
    setLoading(true);
    setError(null);
    setStatusMessage(null);
    try {
      await ensureSecondaryAuthSynced();
      let orderData: DemAnalyticsOrder[];
      let deliveryData: DemAnalyticsDeliveryRow[];
      let cffData: DemAnalyticsCffRow[];
      try {
        [orderData, deliveryData, cffData] = await Promise.all([
          fetchDemAnalyticsOrders({ force }),
          fetchDemAnalyticsDeliveries({ force }),
          fetchDemAnalyticsCff({ force }),
        ]);
      } catch {
        // Soft portal entry can race JWT fan-out — one retry after re-sync.
        await ensureSecondaryAuthSynced();
        [orderData, deliveryData, cffData] = await Promise.all([
          fetchDemAnalyticsOrders({ force: true }),
          fetchDemAnalyticsDeliveries({ force: true }),
          fetchDemAnalyticsCff({ force: true }),
        ]);
      }
      setOrders(orderData);
      setDeliveries(deliveryData);
      setCffRows(cffData);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load analytics data.');
      setOrders([]);
      setDeliveries([]);
      setCffRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(false);
  }, [load]);

  const dailyOrders = useMemo(() => filterOrdersByDate(orders, dailyDate), [orders, dailyDate]);
  const dailyDeliveries = useMemo(
    () => filterDeliveriesByDate(deliveries, dailyDate),
    [deliveries, dailyDate],
  );
  const dailyCff = useMemo(() => filterCffByDate(cffRows, dailyDate), [cffRows, dailyDate]);

  const dailyTrend = useMemo(() => buildLast7DaysTrend(orders, dailyDate), [orders, dailyDate]);
  const dailyHourlyTrend = useMemo(
    () => buildHourlyChannelTrend(dailyOrders),
    [dailyOrders],
  );
  const dailyPeakHour = useMemo(() => peakHourSummary(dailyHourlyTrend), [dailyHourlyTrend]);
  const dailyUserKpi = useMemo(() => buildUserKpi(dailyOrders, true), [dailyOrders]);
  const dailyChannelTotals = useMemo(() => buildChannelTotals(dailyOrders, true), [dailyOrders]);

  const monthlyOrders = useMemo(
    () => filterOrdersByYearMonth(orders, monthlyYear, monthlyMonth),
    [orders, monthlyYear, monthlyMonth],
  );
  const monthlyDeliveries = useMemo(
    () => filterDeliveriesByYearMonth(deliveries, monthlyYear, monthlyMonth),
    [deliveries, monthlyYear, monthlyMonth],
  );
  const monthlyCff = useMemo(
    () => filterCffByYearMonth(cffRows, monthlyYear, monthlyMonth),
    [cffRows, monthlyYear, monthlyMonth],
  );
  const monthlyTrend = useMemo(
    () => buildMonthlyTrendForYear(orders, monthlyYear),
    [orders, monthlyYear],
  );
  const monthlyHourlyTrend = useMemo(
    () => buildHourlyChannelTrend(monthlyOrders),
    [monthlyOrders],
  );
  const monthlyPeakHour = useMemo(() => peakHourSummary(monthlyHourlyTrend), [monthlyHourlyTrend]);
  const monthlyUserKpi = useMemo(() => buildUserKpi(monthlyOrders, false), [monthlyOrders]);
  const monthlyChannelTotals = useMemo(
    () => buildChannelTotals(monthlyOrders, false),
    [monthlyOrders],
  );
  const monthlyDeliveryVolumes = useMemo(
    () => buildDeliveryMonthVolumes(deliveries, monthlyYear),
    [deliveries, monthlyYear],
  );
  const monthlyYearMonth = `${monthlyYear}-${String(monthlyMonth).padStart(2, '0')}`;

  const yearlyOrders = useMemo(
    () => filterOrdersByYear(orders, yearlyYear),
    [orders, yearlyYear],
  );
  const yearlyDeliveries = useMemo(
    () => filterDeliveriesByYear(deliveries, yearlyYear),
    [deliveries, yearlyYear],
  );
  const yearlyCff = useMemo(() => filterCffByYear(cffRows, yearlyYear), [cffRows, yearlyYear]);
  const yearTotals = useMemo(() => buildYearTotals(orders), [orders]);
  const yearLeaderboard = useMemo(
    () => buildYearLeaderboard(orders, yearlyYear),
    [orders, yearlyYear],
  );
  const yearlyUserKpi = useMemo(() => buildUserKpi(yearlyOrders, false), [yearlyOrders]);
  const yearStatus = useMemo(() => buildStatusBreakdown(orders, yearlyYear), [orders, yearlyYear]);
  const yearlyChannelTotals = useMemo(
    () => buildChannelTotals(yearlyOrders, false),
    [yearlyOrders],
  );

  const handleExport = async () => {
    if (orders.length === 0) {
      setStatusMessage('No data to export.');
      return;
    }
    setExportBusy(true);
    setStatusMessage(null);
    try {
      await exportDemAnalyticsOrders(orders);
      setStatusMessage('Detailed CSV exported.');
    } catch (e) {
      setStatusMessage(e instanceof Error ? e.message : 'Export failed.');
    } finally {
      setExportBusy(false);
    }
  };

  const handleSelectDeliveryMonth = (yearMonth: string) => {
    const [y, m] = yearMonth.split('-').map(Number);
    if (!y || !m) return;
    setMonthlyYear(y);
    setMonthlyMonth(m);
  };

  const last7Start = addDaysYmd(dailyDate, -6);

  return (
    <div className="dem-analytics-page dem-analytics-page--floating">
      <div className="dem-analytics-floating-actions">
        <p className="dem-analytics-floating-lead">
          Orders, deliveries, CFF volume and processing time by day, month and year.
        </p>
        <div className="dem-analytics-header-actions">
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => void load(true)}
            disabled={loading}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            type="button"
            className="btn-primary btn-sm"
            onClick={() => void handleExport()}
            disabled={exportBusy || loading || orders.length === 0}
          >
            {exportBusy ? 'Exporting…' : 'Export detailed CSV'}
          </button>
        </div>
      </div>

      {statusMessage ? (
        <p className="dem-uid-status dem-uid-status--ok" role="status">
          {statusMessage}
        </p>
      ) : null}
      {error ? <p className="alert alert-error">{error}</p> : null}

      {loading && !hasData ? (
        <div className="card dem-analytics-loading dem-analytics-loading--inline">
          <p>Loading dashboard…</p>
        </div>
      ) : null}

      {!loading && !hasData && !error ? (
        <div className="card dem-analytics-loading">
          <p>No analytics data available.</p>
        </div>
      ) : null}

      {hasData ? (
        <>
          <div className="dem-analytics-toolbar">
            <div className="dem-analytics-tabs" role="tablist" aria-label="Dashboard reports">
              {TAB_ITEMS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === item.id}
                  className={`dem-filter-btn${tab === item.id ? ' dem-filter-btn--active' : ''}`}
                  onClick={() => setTab(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {tab === 'daily' ? (
              <div className="dem-analytics-controls">
                <label className="dem-analytics-control">
                  <span>Date</span>
                  <input
                    type="date"
                    value={dailyDate}
                    onChange={(e) => setDailyDate(e.target.value)}
                  />
                </label>
              </div>
            ) : null}

            {tab === 'monthly' ? (
              <div className="dem-analytics-controls">
                <label className="dem-analytics-control">
                  <span>Year</span>
                  <select value={monthlyYear} onChange={(e) => setMonthlyYear(Number(e.target.value))}>
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="dem-analytics-control">
                  <span>Month</span>
                  <select
                    value={monthlyMonth}
                    onChange={(e) => setMonthlyMonth(Number(e.target.value))}
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                      <option key={month} value={month}>
                        {monthLabel(month)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            ) : null}

            {tab === 'yearly' ? (
              <div className="dem-analytics-controls">
                <label className="dem-analytics-control">
                  <span>Year</span>
                  <select value={yearlyYear} onChange={(e) => setYearlyYear(Number(e.target.value))}>
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            ) : null}
          </div>

          {tab === 'daily' ? (
            <div className="dem-analytics-tab-panel" role="tabpanel">
              {orders.length > 0 ? (
                <>
                  <DemAnalyticsChannelSummary totals={dailyChannelTotals} showCancelled />

                  <DemAnalyticsCard
                    title="Last 7 days trend"
                    subtitle={`${formatDisplayDate(last7Start)} – ${formatDisplayDate(dailyDate)}`}
                    accent="blue"
                  >
                    <DemAnalyticsTrendChart
                      data={dailyTrend}
                      formatLabel={(label) => trendLabelShort(label, 'day')}
                    />
                  </DemAnalyticsCard>

                  <DemAnalyticsCard
                    title="Orders by hour"
                    subtitle={
                      dailyPeakHour
                        ? `${formatDisplayDate(dailyDate)} · ${dailyPeakHour} · by channel (SGT)`
                        : `${formatDisplayDate(dailyDate)} · by channel (SGT created time)`
                    }
                    accent="blue"
                  >
                    <DemAnalyticsHourlyChannelChart data={dailyHourlyTrend} />
                  </DemAnalyticsCard>

                  <DemAnalyticsCard
                    title="Daily user contribution"
                    subtitle={`Channel breakdown for ${formatDisplayDate(dailyDate)}`}
                    accent="green"
                  >
                    <DemAnalyticsUserChannelChart data={dailyUserKpi} showCancelled />
                  </DemAnalyticsCard>

                  <DemAnalyticsCard
                    title="Daily processing time"
                    subtitle="Total time and average time per timed order"
                    accent="gold"
                  >
                    <DemAnalyticsUserEfficiencyTable data={dailyUserKpi} />
                  </DemAnalyticsCard>
                </>
              ) : null}

              <DemAnalyticsCard
                title="Delivery statistics"
                subtitle={`Arrangements for ${formatDisplayDate(dailyDate)}`}
                accent="purple"
              >
                <DemAnalyticsDeliveryStats
                  periodLabel={formatDisplayDate(dailyDate)}
                  total={dailyDeliveries.length}
                  creators={buildDeliveryCreatorStats(dailyDeliveries)}
                  requestedBy={buildDeliveryRequestedByStats(dailyDeliveries)}
                  issueCausedBy={buildDeliveryIssueCausedByStats(dailyDeliveries)}
                />
              </DemAnalyticsCard>

              <DemAnalyticsCard
                title="CFF statistics by person"
                subtitle={`Customer feedback for ${formatDisplayDate(dailyDate)}`}
                accent="gold"
              >
                <DemAnalyticsCffStats
                  periodLabel={formatDisplayDate(dailyDate)}
                  total={dailyCff.length}
                  persons={buildCffPersonStats(dailyCff)}
                />
              </DemAnalyticsCard>
            </div>
          ) : null}

          {tab === 'monthly' ? (
            <div className="dem-analytics-tab-panel" role="tabpanel">
              {orders.length > 0 ? (
                <>
                  <DemAnalyticsChannelSummary totals={monthlyChannelTotals} />

                  <DemAnalyticsCard
                    title={`${monthlyYear} monthly trend`}
                    subtitle="Imported and cancelled orders by month"
                    accent="blue"
                  >
                    <DemAnalyticsTrendChart
                      data={monthlyTrend}
                      formatLabel={(label) => trendLabelShort(label, 'month')}
                    />
                  </DemAnalyticsCard>

                  <DemAnalyticsCard
                    title="Orders by hour"
                    subtitle={
                      monthlyPeakHour
                        ? `${monthLabel(monthlyMonth)} ${monthlyYear} · ${monthlyPeakHour} · by channel (SGT)`
                        : `${monthLabel(monthlyMonth)} ${monthlyYear} · by channel (SGT created time)`
                    }
                    accent="blue"
                  >
                    <DemAnalyticsHourlyChannelChart data={monthlyHourlyTrend} />
                  </DemAnalyticsCard>

                  <DemAnalyticsCard
                    title="Monthly user contribution"
                    subtitle={`${monthLabel(monthlyMonth)} ${monthlyYear} — imported orders by channel`}
                    accent="green"
                  >
                    <DemAnalyticsUserChannelChart data={monthlyUserKpi} showCancelled={false} />
                  </DemAnalyticsCard>

                  <DemAnalyticsCard
                    title="Monthly processing time"
                    subtitle={`${monthLabel(monthlyMonth)} ${monthlyYear} — total and average per timed order`}
                    accent="gold"
                  >
                    <DemAnalyticsUserEfficiencyTable data={monthlyUserKpi} />
                  </DemAnalyticsCard>
                </>
              ) : null}

              <DemAnalyticsCard
                title="Delivery statistics"
                subtitle={`${displayMonthYear(monthlyYear, monthlyMonth)} arrangements`}
                accent="purple"
              >
                <DemAnalyticsDeliveryStats
                  periodLabel={displayMonthYear(monthlyYear, monthlyMonth)}
                  total={monthlyDeliveries.length}
                  creators={buildDeliveryCreatorStats(monthlyDeliveries)}
                  requestedBy={buildDeliveryRequestedByStats(monthlyDeliveries)}
                  issueCausedBy={buildDeliveryIssueCausedByStats(monthlyDeliveries)}
                  monthVolumes={monthlyDeliveryVolumes}
                  selectedYearMonth={monthlyYearMonth}
                  onSelectYearMonth={handleSelectDeliveryMonth}
                />
              </DemAnalyticsCard>

              <DemAnalyticsCard
                title="CFF statistics by person"
                subtitle={`${displayMonthYear(monthlyYear, monthlyMonth)} customer feedback`}
                accent="gold"
              >
                <DemAnalyticsCffStats
                  periodLabel={displayMonthYear(monthlyYear, monthlyMonth)}
                  total={monthlyCff.length}
                  persons={buildCffPersonStats(monthlyCff)}
                />
              </DemAnalyticsCard>
            </div>
          ) : null}

          {tab === 'yearly' ? (
            <div className="dem-analytics-tab-panel" role="tabpanel">
              {orders.length > 0 ? (
                <>
                  <DemAnalyticsChannelSummary totals={yearlyChannelTotals} />

                  <DemAnalyticsCard
                    title="Year-over-year performance"
                    subtitle="Total imported orders across all years"
                    accent="blue"
                  >
                    <DemAnalyticsYearBarChart data={yearTotals} />
                  </DemAnalyticsCard>

                  <div className="dem-analytics-split">
                    <DemAnalyticsCard
                      title="Yearly user leaderboard"
                      subtitle={`PO / B2B / WhatsApp imports in ${yearlyYear}`}
                      accent="gold"
                    >
                      <DemAnalyticsLeaderboardChart data={yearLeaderboard} />
                    </DemAnalyticsCard>

                    <DemAnalyticsCard
                      title="Overall status"
                      subtitle={`Order health for ${yearlyYear}`}
                      accent="purple"
                    >
                      <DemAnalyticsDonut slices={yearStatus} />
                    </DemAnalyticsCard>
                  </div>

                  <DemAnalyticsCard
                    title="Yearly processing time"
                    subtitle={`${yearlyYear} — total and average per timed order`}
                    accent="green"
                  >
                    <DemAnalyticsUserEfficiencyTable data={yearlyUserKpi} />
                  </DemAnalyticsCard>
                </>
              ) : null}

              <DemAnalyticsCard
                title="Delivery statistics"
                subtitle={`${yearlyYear} arrangements`}
                accent="purple"
              >
                <DemAnalyticsDeliveryStats
                  periodLabel={String(yearlyYear)}
                  total={yearlyDeliveries.length}
                  creators={buildDeliveryCreatorStats(yearlyDeliveries)}
                  requestedBy={buildDeliveryRequestedByStats(yearlyDeliveries)}
                  issueCausedBy={buildDeliveryIssueCausedByStats(yearlyDeliveries)}
                />
              </DemAnalyticsCard>

              <DemAnalyticsCard
                title="CFF statistics by person"
                subtitle={`${yearlyYear} customer feedback`}
                accent="gold"
              >
                <DemAnalyticsCffStats
                  periodLabel={String(yearlyYear)}
                  total={yearlyCff.length}
                  persons={buildCffPersonStats(yearlyCff)}
                />
              </DemAnalyticsCard>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
